/**
 * opncue design system.
 *
 * The palette is derived from the InfluenceCheck web theme:
 * a confident blue, clean whites and a soft grey scale.
 */

import { Platform } from "react-native";

/** Brand palette — single source of truth for every colour used in the app. */
export const Palette = {
  // Blues
  blue: "#007BFF",
  blueDark: "#0056B3",
  blueDeep: "#003E80",
  blueSoft: "#A0CEFF",
  blueTint: "#E6F1FF",
  blueTintStrong: "#D2E6FF",

  // Neutrals
  ink: "#1B2733",
  slate: "#2C3E50",
  grey: "#5A6675",
  greyMuted: "#8A94A3",
  greyLine: "#E2E8F0",
  cloud: "#F4F7FB",
  mist: "#F8FAFC",
  white: "#FFFFFF",

  // Status
  green: "#22A06B",
  greenTint: "#E4F6EE",
  amber: "#E0A800",
  red: "#E5484D",
  redTint: "#FCEBEC",
} as const;

export const Colors = {
  light: {
    text: Palette.ink,
    textSecondary: Palette.grey,
    textMuted: Palette.greyMuted,
    background: Palette.mist,
    surface: Palette.white,
    surfaceAlt: Palette.cloud,
    backgroundElement: Palette.cloud,
    backgroundSelected: Palette.blueTint,
    border: Palette.greyLine,
    tint: Palette.blue,
    tintStrong: Palette.blueDark,
    onTint: Palette.white,
    success: Palette.green,
    successTint: Palette.greenTint,
    danger: Palette.red,
    dangerTint: Palette.redTint,
  },
  dark: {
    text: "#F5F8FC",
    textSecondary: "#AEB8C4",
    textMuted: "#7E8A99",
    background: "#0B1622",
    surface: "#13202E",
    surfaceAlt: "#1A2A3A",
    backgroundElement: "#1A2A3A",
    backgroundSelected: "#1E3A5F",
    border: "#243748",
    tint: "#4E9DFF",
    tintStrong: "#7FBBFF",
    onTint: "#04111F",
    success: "#3BCB8C",
    successTint: "#11362A",
    danger: "#FF6B70",
    dangerTint: "#3A1A1C",
  },
} as const;

export type ThemeColors = { [K in keyof typeof Colors.light]: string };
export type ThemeColor = keyof typeof Colors.light & keyof typeof Colors.dark;

/** Brand gradient pairs (use with expo-linear-gradient or as solid fallbacks). */
export const Gradients = {
  brand: [Palette.blue, Palette.blueDark] as const,
  brandDeep: [Palette.blueDark, Palette.blueDeep] as const,
  sky: [Palette.blueTint, Palette.white] as const,
};

export const Spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

export const Radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 999,
} as const;

export const FontSize = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 22,
  xxl: 28,
  xxxl: 34,
} as const;

export const Fonts = Platform.select({
  ios: {
    sans: "system-ui",
    serif: "ui-serif",
    rounded: "ui-rounded",
    mono: "ui-monospace",
  },
  default: {
    sans: "normal",
    serif: "serif",
    rounded: "normal",
    mono: "monospace",
  },
  web: {
    sans: "var(--font-display)",
    serif: "var(--font-serif)",
    rounded: "var(--font-rounded)",
    mono: "var(--font-mono)",
  },
}) as { sans: string; serif: string; rounded: string; mono: string };

export const MaxContentWidth = 560;
